import { motion } from "framer-motion";
import { Briefcase, TrendingUp, ShieldCheck, Boxes, Globe, Headphones } from "lucide-react";

const items = [
  {
    Icon: Briefcase,
    title: "Strategic Management Backbone",
    body: "We do not just move products; we manage them. The legacy of Ms. Dijamco's 2013 firm ensures that every logistical step is optimized, cost-effective, and flawlessly executed.",
    size: "lg",
  },
  { Icon: TrendingUp, title: "Dynamic Market Agility", body: "Modern trading strategies that adapt quickly to consumer trends — partners always get the right products at the right time." },
  { Icon: ShieldCheck, title: "Uncompromising Quality", body: "Trusted manufacturers, vetted brands, and strict quality controls on every shipment." },
  {
    Icon: Boxes,
    title: "Diverse Product Portfolio",
    body: "Continuously expanding the range of imported and domestic FMCG categories — food, personal care, household, baby, beauty, and more.",
    size: "lg",
  },
  { Icon: Globe, title: "Regional Reach", body: "From Metro Manila to province-level sub-distributors, our network covers the country." },
  { Icon: Headphones, title: "Dedicated Account Service", body: "Direct line to a consultative team for trade marketing, replenishment, and credit terms." },
];

export default function WhyChooseUs() {
  return (
    <section data-testid="why-choose-us-section" className="py-24 md:py-32 bg-slate-950 text-white relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.18),_transparent_50%)]" />
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.18),_transparent_50%)]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mb-14"
        >
          <p className="text-sm uppercase tracking-widest font-semibold text-emerald-400 mb-3">Why Choose Us</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold">
            Built on three uncompromising <span className="text-emerald-400">pillars</span>.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-[200px]">
          {items.map(({ Icon, title, body, size }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              data-testid={`why-choose-${title.toLowerCase().replace(/\s+/g, "-")}`}
              className={`bg-white/5 hover:bg-white/10 backdrop-blur border border-white/10 rounded-3xl p-7 flex flex-col justify-between transition-colors ${
                size === "lg" ? "lg:col-span-2 lg:row-span-2" : ""
              }`}
            >
              <span className={`inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-white/10 text-emerald-300 ${size === "lg" ? "w-14 h-14" : "w-11 h-11"}`}>
                <Icon size={size === "lg" ? 24 : 20} />
              </span>
              <div>
                <h3 className={`font-heading font-semibold text-white mb-2 ${size === "lg" ? "text-2xl" : "text-lg"}`}>{title}</h3>
                <p className={`text-slate-400 leading-relaxed ${size === "lg" ? "text-base" : "text-sm"}`}>{body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
